import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type PERentLicenseType = 'single_300' | 'sub_24' | null;

/** BWA-Betriebskosten-Keys, die für Stuhlmiete umlegbar sein können */
export const PE_RENT_BWA_KEYS = [
  'raumkosten',
  'betriebssteuern',
  'versicherungen',
  'besondereKosten',
  'kfzKosten',
  'werbeReisekosten',
  'warenabgabe',
  'reparatur',
  'sonstigeKosten',
  'kredite',
  'zinsaufwendungen',
  'gewerbesteuer',
  'koerperschaftssteuer',
  'budgetPlansumme',
  'preisExpert',
  'premiumSoftware',
  'sonstigeEintrag1',
  'sonstigeEintrag2',
  'sonstigeEintrag3',
  'sonstigeEintrag4',
] as const;

export type PERentBwaKey = (typeof PE_RENT_BWA_KEYS)[number];

export interface PERentManualCost {
  id: string;
  label: string;
  monthlyEuro: number;
  included: boolean;
  /** true = Betrag wird von der Kostenbasis abgezogen (z. B. Seminare in sonstige Kosten) */
  isDeduction: boolean;
}

/** Standard: typische raumbezogene Kosten an, Rest aus */
export const defaultIncludedBwaKeys = (): Record<string, boolean> => {
  const map: Record<string, boolean> = {};
  PE_RENT_BWA_KEYS.forEach((key) => {
    map[key] = [
      'raumkosten',
      'versicherungen',
      'besondereKosten',
      'reparatur',
      'sonstigeKosten',
    ].includes(key);
  });
  return map;
};

export interface PERentState {
  isLicensed: boolean;
  licenseType: PERentLicenseType;
  /** Admin-PIN PW90: darf ausgeschlossene Kosten manuell zuschalten */
  isPinUnlocked: boolean;
  includedBwaKeys: Record<string, boolean>;
  /** Admin: frei benannte Monatskosten (nicht aus BWA) */
  manualCosts: PERentManualCost[];
  /** Optional: manueller Stundensatz (null = kalkulierter Wert) — nur sinnvoll mit Mieterstunden */
  hourlyFactorOverride: number | null;
  /** Stunden des Stuhl-/Platzmieters pro Monat */
  tenantMonthlyHours: number;
  /** Optional: Wochenstunden-Eingabe (wird × 4,33) */
  tenantWeeklyHours: number;
  hoursInputMode: 'month' | 'week';

  verifyPin: (pin: string) => boolean;
  lockPin: () => void;
  toggleBwaKey: (key: string) => void;
  addManualCost: (label: string, monthlyEuro: number, isDeduction?: boolean) => void;
  updateManualCost: (
    id: string,
    patch: Partial<Pick<PERentManualCost, 'label' | 'monthlyEuro' | 'isDeduction'>>
  ) => void;
  toggleManualCost: (id: string) => void;
  toggleManualDeduction: (id: string) => void;
  removeManualCost: (id: string) => void;
  setHourlyFactorOverride: (value: number | null) => void;
  setHoursInputMode: (mode: 'month' | 'week') => void;
  setTenantMonthlyHours: (hours: number) => void;
  setTenantWeeklyHours: (hours: number) => void;
  setLicense: (type: PERentLicenseType) => void;
  cancelLicense: () => void;
  resetRentState: () => void;
}

const WEEKS_PER_MONTH = 4.33;

const newManualId = () =>
  `manual_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export const usePERentStore = create<PERentState>()(
  persist(
    (set) => ({
      isLicensed: false,
      licenseType: null,
      isPinUnlocked: false,
      includedBwaKeys: defaultIncludedBwaKeys(),
      manualCosts: [],
      hourlyFactorOverride: null,
      tenantMonthlyHours: 0,
      tenantWeeklyHours: 0,
      hoursInputMode: 'week',

      verifyPin: (pin: string) => {
        const isValid = pin === 'PW90' || pin === '90';
        if (isValid) set({ isPinUnlocked: true });
        return isValid;
      },

      lockPin: () => set({ isPinUnlocked: false }),

      toggleBwaKey: (key) =>
        set((state) => ({
          includedBwaKeys: {
            ...state.includedBwaKeys,
            [key]: !state.includedBwaKeys[key],
          },
        })),

      addManualCost: (label, monthlyEuro, isDeduction = false) =>
        set((state) => ({
          manualCosts: [
            ...state.manualCosts,
            {
              id: newManualId(),
              label: label.trim() || (isDeduction ? 'Abzug' : 'Manuelle Kosten'),
              monthlyEuro: Math.max(0, Number(monthlyEuro) || 0),
              included: true,
              isDeduction: !!isDeduction,
            },
          ],
        })),

      updateManualCost: (id, patch) =>
        set((state) => ({
          manualCosts: state.manualCosts.map((c) =>
            c.id === id
              ? {
                  ...c,
                  ...(patch.label !== undefined ? { label: patch.label } : {}),
                  ...(patch.monthlyEuro !== undefined
                    ? { monthlyEuro: Math.max(0, Number(patch.monthlyEuro) || 0) }
                    : {}),
                  ...(patch.isDeduction !== undefined
                    ? { isDeduction: !!patch.isDeduction }
                    : {}),
                }
              : c
          ),
        })),

      toggleManualCost: (id) =>
        set((state) => ({
          manualCosts: state.manualCosts.map((c) =>
            c.id === id ? { ...c, included: !c.included } : c
          ),
        })),

      toggleManualDeduction: (id) =>
        set((state) => ({
          manualCosts: state.manualCosts.map((c) =>
            c.id === id ? { ...c, isDeduction: !c.isDeduction } : c
          ),
        })),

      removeManualCost: (id) =>
        set((state) => ({
          manualCosts: state.manualCosts.filter((c) => c.id !== id),
        })),

      setHourlyFactorOverride: (value) =>
        set({
          hourlyFactorOverride:
            value === null || Number.isNaN(value) || Number(value) <= 0
              ? null
              : Number(value),
        }),

      setHoursInputMode: (mode) => set({ hoursInputMode: mode }),

      setTenantMonthlyHours: (hours) => {
        const monthly = Number(hours) || 0;
        set({
          tenantMonthlyHours: monthly,
          tenantWeeklyHours: Number((monthly / WEEKS_PER_MONTH).toFixed(2)),
          ...(monthly <= 0 ? { hourlyFactorOverride: null } : {}),
        });
      },

      setTenantWeeklyHours: (hours) => {
        const weekly = Number(hours) || 0;
        set({
          tenantWeeklyHours: weekly,
          tenantMonthlyHours: Number((weekly * WEEKS_PER_MONTH).toFixed(2)),
          ...(weekly <= 0 ? { hourlyFactorOverride: null } : {}),
        });
      },

      setLicense: (type) => {
        set({
          licenseType: type,
          isLicensed: !!type,
        });
        if (type === 'single_300' || type === 'sub_24') {
          void (async () => {
            try {
              const useAppStore = (await import('./index')).default;
              const { registerPremiumToolBooking, resolvePERentBooking } = await import(
                '../utils/premiumCostBridge'
              );
              await registerPremiumToolBooking(resolvePERentBooking(type));
              useAppStore.getState().unlockTool('pe-rent');
            } catch (err) {
              console.error('PERent Lizenz-Kostenbuchung fehlgeschlagen:', err);
            }
          })();
        }
      },

      cancelLicense: () => {
        void (async () => {
          try {
            const useAppStore = (await import('./index')).default;
            const { isPremiumCancelAllowed } = await import('../utils/premiumCostBridge');

            const activatedAt =
              (useAppStore.getState().clientData as {
                premiumToolActivatedAt?: Record<string, string>;
              } | null)?.premiumToolActivatedAt?.['pe-rent'] || null;

            if (!isPremiumCancelAllowed(activatedAt)) {
              console.warn('PERent-Cancel abgelehnt: Mindestlaufzeit 12 Monate nicht erreicht');
              return;
            }

            set({
              licenseType: null,
              isLicensed: false,
            });
            useAppStore.getState().cancelSubscription('pe-rent');
          } catch (err) {
            console.error('PERent Lizenz-Kündigung fehlgeschlagen:', err);
          }
        })();
      },

      resetRentState: () =>
        set({
          isPinUnlocked: false,
          includedBwaKeys: defaultIncludedBwaKeys(),
          manualCosts: [],
          hourlyFactorOverride: null,
          tenantMonthlyHours: 0,
          tenantWeeklyHours: 0,
          hoursInputMode: 'week',
        }),
    }),
    {
      name: 'pe-rent-calc',
      version: 4,
      migrate: (persisted: unknown) => {
        const state = (persisted || {}) as Record<string, unknown>;
        const costs = Array.isArray(state.manualCosts) ? state.manualCosts : [];
        return {
          ...state,
          tenantMonthlyHours: Number(state.tenantMonthlyHours) > 0 ? state.tenantMonthlyHours : 0,
          tenantWeeklyHours: Number(state.tenantWeeklyHours) > 0 ? state.tenantWeeklyHours : 0,
          hourlyFactorOverride: null,
          manualCosts: costs.map((c: Record<string, unknown>) => ({
            ...c,
            isDeduction: !!c.isDeduction,
          })),
        };
      },
      partialize: (state) => ({
        includedBwaKeys: state.includedBwaKeys,
        manualCosts: state.manualCosts,
        hourlyFactorOverride: state.hourlyFactorOverride,
        tenantMonthlyHours: state.tenantMonthlyHours,
        tenantWeeklyHours: state.tenantWeeklyHours,
        hoursInputMode: state.hoursInputMode,
      }),
    }
  )
);

export default usePERentStore;
